import { useGetSponsorshipDetailMutation } from "src/services/sponsorshipsServices";
import {
  LargeHeadingStyled,
  SectionHeaderStyled,
} from "../../../components/custom/texts/texts.styled";
import { Card, Flex } from "@nubeteck/components";
import { Layout, Table, Button, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect } from "react";
import { IPatrocinioEstadoCuenta } from "src/Interfaces/sponsorship";
import { useNavigate, useParams } from "react-router-dom";
import { FormatterUtils } from "@nubeteck/utils";
import { getSponsorAutoservicePage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { formatDate } from "src/utils/date-util";

const { Content } = Layout;

const SponsorshipDetail: React.FC = () => {
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);

  const [getSponsorshipDetail, { data, isLoading: isLoadingData }] = useGetSponsorshipDetailMutation();

  const navigate = useNavigate();
  const { id } = useParams();
  const { locale } = useLocale();
  const [sponsorAutoservicePage, setSponsorAutoservicePage] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [hasError, setHasError] = React.useState<boolean>(false);
  useEffect(() => {
    getSponsorshipDetail(id);
  }, [getSponsorshipDetail, id]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoading(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSponsorAutoservicePage(null);
        setIsLoading(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSponsorAutoservicePage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSponsorAutoservicePage(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSponsorAutoservicePage(null);
          setIsLoading(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const columns = React.useMemo<ColumnsType<IPatrocinioEstadoCuenta>>(
    () => [
      {
        title: isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail_table?.[0]?.label || "Monto original"),
        dataIndex: "montoOriginal",
        width: 150,
        render: (_, record) => FormatterUtils.number(record.montoOriginal),
      },
      {
        title: isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail_table?.[1]?.label || "Fecha de uso"),
        dataIndex: "fechaMovimiento",
        width: 150,
        render: (value) => formatDate(value),
      },
      {
        title: isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail_table?.[2]?.label || "Monto utilizado"),
        dataIndex: "monto",
        width: 150,
        render: (_, record) => FormatterUtils.number(record.monto),
      },
      {
        title: isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail_table?.[3]?.label || "Monto total utilizado"),
        dataIndex: "montoUtilizadoAcumulado",
        width: 120,
        render: (_, record) => FormatterUtils.number(record.montoUtilizadoAcumulado),
      },
      {
        title: isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail_table?.[4]?.label || "Monto restante"),
        dataIndex: "montoRestante",
        width: 120,
        render: (_, record) => FormatterUtils.number(record.montoRestante),
      },
    ],
    [isLoading, sponsorAutoservicePage?.sponsor_detail_table],
  );

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoading(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoading(false);
        setSponsorAutoservicePage(null);
      }}
    />;
  }

  return (
    <Flex style={{ flexDirection: "column", width: "100%", alignSelf: "flex-start" }}>
      <SectionHeaderStyled>
        <LargeHeadingStyled>
          {isLoading ? <Skeleton.Input active size="large" style={{ width: 300 }} /> : (sponsorAutoservicePage?.sponsor_detail?.titleO || "Detalle de patrocinio ")} {!isLoading && data?.data?.patrocinioNombre} {isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail?.titleT || "para el estudiante")}{" "}
          {!isLoading && data?.data?.estudianteNombre}
        </LargeHeadingStyled>
        <Button title="" type="default" onClick={() => navigate("/admin/sponsorships")}>
          {isLoading ? <Skeleton.Input active size="default" style={{ width: 150 }} /> : (sponsorAutoservicePage?.sponsor_detail?.before_button || "Volver atrás")}
        </Button>
      </SectionHeaderStyled>
      <Content style={{ marginTop: "16px" }}>
        <Card bodyStyle={{ padding: 0, width: "100%" }}>
          <Table<IPatrocinioEstadoCuenta>
            loading={isLoading || isLoadingData}
            columns={columns}
            dataSource={!isLoading ? data?.data?.detalle : []}
            rowKey={(v) => `${v.estudiantePatrocinioId}-${v.movimientoId}`}
            pagination={{
              onChange(page, pageSize) {
                setPageNumber(page);
                setRowsCount(pageSize);
              },
              pageSize: rowsCount,
              current: pageNumber,
              total: data?.data?.detalle?.length ?? 0,
              showSizeChanger: true,
              style: { marginRight: "30px" },
            }}
            scroll={{ x: 300 }}
          />
        </Card>
      </Content>
    </Flex>
  );
};

export default SponsorshipDetail;
